import { useEffect, useRef, useState } from "react";
import type { Route } from "./+types/home";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  const origin = new URL(request.url).origin;
  return { locale, origin };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].home.meta;
  const ogImage = data?.origin ? `${data.origin}/resources/og-home` : undefined;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/", locale, image: ogImage }),
  ];
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

const ROW_A = [
  "FD001-251225032", "FD001-251225033", "FD001-251225034",
  "FD002-251014004", "FD002-260210001", "FD012-251211019",
  "FD016-251224017", "FD016-251224019", "FD016-251224021",
  "FD028-251224009", "FD028-251224010",
];
const ROW_B = [
  "FD028-251224011", "FD029-251231031", "FD030-251223016",
  "FD033-251225049", "FD033-251225050", "FD033-251225052",
  "FD036-251231032", "FD037-251225025", "FD040-251231021",
  "FD051-251226013", "FD064-260105031",
];

function ImageMarqueeRow({ skus, direction = "left", duration = 60 }: { skus: string[]; direction?: "left" | "right"; duration?: number }) {
  const doubled = [...skus, ...skus];
  return (
    <div className="overflow-hidden w-full">
      <div className="flex gap-3 w-max" style={{ animation: `${direction === "left" ? "marquee-left" : "marquee-right"} ${duration}s linear infinite` }}>
        {doubled.map((sku, i) => (
          <div
            key={`${sku}-${i}`}
            className="shrink-0 hover:border-primary/30 hover:border transition-colors"
            style={{
              width: "160px",
              aspectRatio: "2/3",
              transform: i % 2 === 0 ? "rotate(1.5deg)" : "rotate(-1.5deg)",
            }}
          >
            <img src={`/designs/${sku}/001.jpg`} alt="" aria-hidden="true" className="w-full h-full object-contain" loading="lazy" style={{ mixBlendMode: "multiply" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimatedCounter({ target, suffix = "", delay = 0, started }: { target: number; suffix?: string; delay?: number; started: boolean }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started) return;
    const timeout = setTimeout(() => {
      const duration = 1500;
      const start = performance.now();
      const step = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(timeout);
  }, [started, target, delay]);

  return <>{started ? value : 0}{suffix}{target === 48 ? "+" : ""}</>;
}

export default function LandingPage() {
  const { t } = useT();
  const h = t.home;
  const reducedMotion = useReducedMotion();

  // Reveal refs
  const line1Ref = useReveal(0.3);
  const line2Ref = useReveal(0.3);
  const line3Ref = useReveal(0.3);
  const processRef = useReveal(0.15);
  const showcaseRef = useReveal(0.1);
  const quoteRef = useReveal();
  const forkRef = useReveal(0.15);
  const marqueeRef = useReveal(0.1);

  // Stats animated counters
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsStarted, setStatsStarted] = useState(false);
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <MarketingLayout transparentNav>

      {/* ══ SECTION 1: HERO — Full-bleed background + Clarity + CTA ══ */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight: "100vh", color: "oklch(0.96 0.005 90)" }}
      >
        {/* Background image — full bleed with Ken Burns */}
        <img
          src="https://images.unsplash.com/photo-1515562141589-67f0d569b6b1?auto=format&fit=crop&w=1920&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            animation: reducedMotion ? "none" : "ken-burns 20s ease-in-out infinite alternate",
            filter: "saturate(0.9)",
          }}
        />
        {/* Dark overlay for text legibility */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, oklch(0.06 0.005 50 / 0.75) 0%, oklch(0.06 0.005 50 / 0.65) 50%, oklch(0.06 0.005 50 / 0.85) 100%)" }} />

        {/* BrilliantCut ambient — subtle on top of the overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={600} opacity={0.03} animated stroke="oklch(0.92 0.005 90)" />
        </div>

        <div className="relative text-center z-10 px-6 max-w-3xl mx-auto">
          <h1
            className="font-display font-light tracking-[0.32em] uppercase"
            style={{
              fontSize: "clamp(2.4rem, 6vw, 5.5rem)",
              animation: reducedMotion ? "none" : "fade-in-up 1.2s ease-out both",
              textShadow: "0 2px 20px oklch(0 0 0 / 0.3)",
            }}
          >
            Caratzon
          </h1>

          {/* Value prop — what this IS */}
          <p
            className="mt-5 mx-auto"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "clamp(0.78rem, 1.1vw, 0.95rem)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "oklch(0.92 0.005 90 / 0.6)",
              animation: reducedMotion ? "none" : "fade-in-up 1s ease-out 0.3s both",
            }}
          >
            {h.emergence.subtitle}
          </p>

          {/* Tagline — poetic line */}
          <p
            className="font-display font-light italic mt-8 mx-auto"
            style={{
              fontSize: "clamp(1.1rem, 2vw, 1.6rem)",
              lineHeight: 1.4,
              color: "oklch(0.92 0.005 90 / 0.7)",
              maxWidth: "36ch",
              animation: reducedMotion ? "none" : "fade-in-up 1s ease-out 0.6s both",
            }}
          >
            {h.emergence.tagline}
          </p>

          {/* CTAs */}
          <div
            className="mt-10 flex flex-col sm:flex-row gap-3 justify-center"
            style={{ animation: reducedMotion ? "none" : "fade-in-up 1s ease-out 0.9s both" }}
          >
            <a
              href="/sign-in?redirect=/trade/catalog"
              className="inline-block px-8 py-4 transition-all duration-300 hover:tracking-[0.28em]"
              style={{
                fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase",
                background: "var(--primary)", color: "var(--primary-foreground)",
              }}
            >
              {h.emergence.cta}
            </a>
            <a
              href="/brands/how-it-works"
              className="inline-block px-8 py-4 border transition-all duration-300 hover:tracking-[0.28em]"
              style={{
                fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase",
                borderColor: "oklch(0.92 0.005 90 / 0.3)", color: "oklch(0.92 0.005 90 / 0.75)",
              }}
            >
              {h.emergence.ctaSecondary}
            </a>
          </div>
        </div>

        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          style={{ animation: reducedMotion ? "none" : "scroll-hint 2s ease-in-out infinite" }}
          aria-label="Scroll to explore"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
            <path d="M12 4v14M5 18l7 7 7-7" />
          </svg>
        </div>
      </section>

      {/* ══ SECTION 2: STORY BEAT + INLINE SPLIT ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-28" style={{ background: "var(--background)" }}>
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center gap-6">
          {[
            { ref: line1Ref, text: h.storyBeat.line1, delay: "0s" },
            { ref: line2Ref, text: h.storyBeat.line2, delay: "0.15s" },
            { ref: line3Ref, text: h.storyBeat.line3, delay: "0.3s" },
          ].map(({ ref, text, delay }) => (
            <div key={text} ref={ref} className="reveal" style={{ transitionDelay: delay }}>
              <p
                className="font-display font-light italic"
                style={{ fontSize: "clamp(1.8rem, 4.5vw, 4rem)", lineHeight: 1.2, color: "var(--foreground)" }}
              >
                {text}
              </p>
            </div>
          ))}

          {/* Inline audience split — early escape hatch */}
          <div className="mt-8 flex items-center gap-4">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)", color: "var(--muted-foreground)" }}>
              {h.storyBeat.splitLabel}
            </span>
            <a
              href="/designers"
              className="inline-block px-6 py-3 border border-border text-foreground transition-all duration-300 hover:border-primary hover:text-primary"
              style={{ fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              {h.storyBeat.splitDesigner}
            </a>
            <a
              href="/brands"
              className="inline-block px-6 py-3 border border-border text-foreground transition-all duration-300 hover:border-teal hover:text-teal"
              style={{ fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              {h.storyBeat.splitBrand}
            </a>
          </div>
        </div>
      </section>

      {/* ══ SECTION 3: HOW IT WORKS — 3-step teaser ══ */}
      <section className="px-6 lg:px-12 py-14 lg:py-20" style={{ background: "var(--background)" }}>
        <div ref={processRef} className="reveal max-w-5xl mx-auto">
          <p className="issue-label text-center mb-10 lg:mb-14" style={{ fontFamily: "var(--font-sans)" }}>
            {h.process.overline}
          </p>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
            {h.process.steps.map((step: { number: string; heading: string; body: string; hook: string }) => (
              <div key={step.number} className="text-center">
                <span
                  className="font-display font-light block mb-3"
                  style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "var(--primary)", opacity: 0.3 }}
                >
                  {step.number}
                </span>
                <h3
                  className="font-display font-medium text-foreground mb-3"
                  style={{ fontSize: "clamp(1.3rem, 2vw, 1.8rem)" }}
                >
                  {step.heading}
                </h3>
                <p
                  className="text-muted-foreground leading-relaxed mx-auto"
                  style={{ fontSize: "0.88rem", maxWidth: "30ch" }}
                >
                  {step.body}
                </p>
                <p
                  className="mt-3 mx-auto"
                  style={{ fontSize: "0.75rem", fontFamily: "var(--font-sans)", letterSpacing: "0.08em", color: "var(--primary)", opacity: 0.7, maxWidth: "28ch" }}
                >
                  {step.hook}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ SECTION 4: PRODUCT STRIP — horizontal showcase ══ */}
      <section ref={showcaseRef} className="reveal px-6 lg:px-12 py-14 lg:py-20" style={{ background: "var(--background)" }}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {h.showcase.pieces.map((piece: { sku: string; designer: string; name: string; descriptor: string }) => (
            <div key={piece.sku} className="text-center group">
              <div className="mb-4 overflow-hidden" style={{ aspectRatio: "3/4" }}>
                <img
                  src={`/designs/${piece.sku}/001.jpg`}
                  alt={`${piece.name} by ${piece.designer}`}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  style={{ mixBlendMode: "multiply" }}
                  loading="lazy"
                />
              </div>
              <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}>
                {piece.designer}
              </span>
              <h3 className="font-display font-medium text-foreground" style={{ fontSize: "clamp(0.95rem, 1.5vw, 1.15rem)" }}>
                {piece.name}
              </h3>
              <p className="font-display italic text-muted-foreground mt-1" style={{ fontSize: "0.82rem" }}>
                {piece.descriptor}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ SECTION 5: SOCIAL PROOF — teal stats + quote ══ */}
      <section className="home-teal">
        <div ref={statsRef} className="px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 max-w-5xl mx-auto">
            {h.stats.map((stat: { value: string; numericValue: number; label: string; suffix?: string }, i: number) => (
              <div key={stat.label} className="relative text-center py-6">
                {i > 0 && (
                  <div
                    className="hidden sm:block absolute inset-y-2 left-0 w-px"
                    style={{
                      background: "oklch(0.96 0.005 90 / 0.2)",
                      transformOrigin: "top",
                      animation: statsStarted && !reducedMotion ? `divider-draw 0.8s ease-out ${0.2 + i * 0.15}s both` : "none",
                    }}
                  />
                )}
                <p className="font-display font-light leading-none mb-2" style={{ fontSize: "clamp(3rem, 7vw, 6rem)" }}>
                  <AnimatedCounter target={stat.numericValue} suffix={stat.suffix ?? ""} delay={i * 200} started={statsStarted} />
                </p>
                <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.7 }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div ref={quoteRef} className="reveal px-6 lg:px-12 pb-16 lg:pb-24">
          <div className="max-w-3xl mx-auto">
            <p
              className="font-display font-light italic"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", lineHeight: 1.35, borderLeft: "3px solid var(--primary)", paddingLeft: "1.5rem" }}
            >
              {h.quote.text.split("\n").map((line: string, i: number) => (
                <span key={i}>{line}{i === 0 && <><br className="hidden lg:inline" />{" "}</>}</span>
              ))}
            </p>
            <div className="mt-8 flex items-center gap-6">
              <div className="w-16" style={{ height: "1px", background: "oklch(0.96 0.005 90 / 0.3)" }} />
              <span className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.7 }}>{h.quote.attribution}</span>
              <div className="w-16" style={{ height: "1px", background: "oklch(0.96 0.005 90 / 0.3)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION 6: THE FORK ══ */}
      <section className="px-6 lg:px-12 py-12 lg:py-24" style={{ background: "var(--background)" }}>
        <p
          className="font-display font-light italic text-center text-muted-foreground mb-8 lg:mb-14"
          style={{ fontSize: "clamp(1.2rem, 2.5vw, 2rem)" }}
        >
          {h.fork.heading}
        </p>

        <div ref={forkRef} className="reveal grid lg:grid-cols-2 gap-4 max-w-6xl mx-auto fork-grid">
          <a href="/designers" className="group relative block overflow-hidden" style={{ aspectRatio: "4/3" }}>
            <img src="https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?auto=format&fit=crop&w=800&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ animation: "ken-burns 15s ease-in-out infinite alternate" }} loading="lazy" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.08 0 0 / 0.8), transparent 60%)" }} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="issue-label block mb-3" style={{ fontFamily: "var(--font-sans)", color: "oklch(0.78 0.10 52)", textShadow: "0 1px 3px oklch(0 0 0 / 0.5)" }}>{h.fork.designer.eyebrow}</span>
              <p className="font-display font-light text-white mb-6" style={{ fontSize: "clamp(1.5rem, 3vw, 2.4rem)" }}>{h.fork.designer.line}</p>
              <span className="inline-block px-8 py-4 bg-white/10 border border-white/30 text-white transition-all duration-300 group-hover:tracking-[0.28em] group-hover:bg-white/10 lg:opacity-0 lg:group-hover:opacity-100" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.fork.designer.cta}</span>
            </div>
          </a>

          <a href="/brands" className="group relative block overflow-hidden" style={{ aspectRatio: "4/3" }}>
            <img src="https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?auto=format&fit=crop&w=800&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center top", animation: "ken-burns 15s ease-in-out infinite alternate" }} loading="lazy" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.08 0 0 / 0.8), transparent 60%)" }} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="issue-label block mb-3" style={{ fontFamily: "var(--font-sans)", color: "oklch(0.72 0.10 213)", textShadow: "0 1px 3px oklch(0 0 0 / 0.5)" }}>{h.fork.brand.eyebrow}</span>
              <p className="font-display font-light text-white mb-6" style={{ fontSize: "clamp(1.5rem, 3vw, 2.4rem)" }}>{h.fork.brand.line}</p>
              <span className="inline-block px-8 py-4 bg-white/10 border border-white/30 text-white transition-all duration-300 group-hover:tracking-[0.28em] group-hover:bg-white/10 lg:opacity-0 lg:group-hover:opacity-100" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.fork.brand.cta}</span>
            </div>
          </a>
        </div>
      </section>

      {/* ══ SECTION 7: MARQUEE + CLOSE ══ */}
      <section ref={marqueeRef} className="reveal py-8 space-y-3" aria-label={h.marquee.ariaLabel}>
        <ImageMarqueeRow skus={ROW_A} direction="left" duration={45} />
        <ImageMarqueeRow skus={ROW_B} direction="right" duration={55} />
      </section>

      <section
        className="home-dark relative overflow-hidden text-center"
        style={{ padding: "clamp(5rem, 12vh, 10rem) 1.5rem clamp(8rem, 16vh, 14rem)" }}
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={700} opacity={0.06} animated stroke="oklch(0.92 0.005 90)" />
        </div>
        <div className="relative z-10">
          <h2 className="font-display font-light tracking-[0.28em] uppercase mb-8" style={{ fontSize: "clamp(1.2rem, 3vw, 2.2rem)" }}>Caratzon</h2>
          <p className="font-display font-light italic mb-12" style={{ fontSize: "clamp(1.5rem, 3.5vw, 3rem)", lineHeight: 1.2 }}>{h.close.line}</p>
          <a
            href="/brands"
            className="inline-block px-10 py-5 transition-all duration-300 hover:tracking-[0.28em]"
            style={{ fontSize: "0.72rem", letterSpacing: "0.2em", textTransform: "uppercase", background: "var(--primary)", color: "var(--primary-foreground)" }}
          >
            {h.close.cta}
          </a>
        </div>
      </section>

    </MarketingLayout>
  );
}
